/** Adapter: GraphQL Catalog rows -> engine CatalogSnapshot (via the seed shape,
 *  reusing snapshotFromSeed so there is exactly one assembly semantics).
 *  Version precedence and time resolution both live in resolveAt. */
import { CatalogSeed, snapshotFromSeed } from './seedBuilder';
import { CatalogSnapshot } from './engine';
import type { CatalogComponentRow, CatalogCompositionRow } from './__fixtures__/catalogRows';

/** Rows -> CatalogSeed. No quarter pre-filter: resolveAt needs earlier rows in
 *  order to carry them forward, so every row for the vendor passes through.
 *  Extracted so the publish path can build the seed ONCE and snapshot it per
 *  quarter, instead of re-converting 344 rows fourteen times. */
export function seedFromRows(
  components: CatalogComponentRow[], compositions: CatalogCompositionRow[],
): CatalogSeed {
  return {
    rows: components.map(r => ({
      vendor: r.vendor, effectiveFrom: r.effectiveFrom,
      componentSku: r.componentSku, kind: r.kind as CatalogSeed['rows'][number]['kind'],
      value: r.value, recordedAt: r.recordedAt, version: r.version,
    })),
    // Every composition version passes through; snapshotFromSeed picks, per
    // quarter, the highest version whose components are ALL costed there.
    // Full rationale: see snapshotFromSeed in seedBuilder.ts.
    compositions: compositions.map(r => ({
      vendor: r.vendor, productSku: r.productSku,
      components: JSON.parse(r.components), version: r.version })),
  };
}

export function snapshotFromRows(
  vendor: string, components: CatalogComponentRow[], compositions: CatalogCompositionRow[],
  quarter: string, asOf?: string,
): CatalogSnapshot {
  const resolved = snapshotFromSeed(seedFromRows(components, compositions), quarter, asOf);
  // This is the live-GraphQL-catalog seam, not snapshotFromSeed: there, an
  // empty resolution is legitimate (a synthetic/build-time seed queried
  // before its first-ever authored quarter — see snapshotFromSeed's own doc
  // and generateProductDb.test.ts's "never priced" case). Here, `components`
  // comes straight off the wire for one vendor, so "nothing resolved" always
  // means either an unseeded vendor (e.g. DELL) or a quarter outside the
  // seeded range — a fault, not a valid state, and it must fail loudly
  // rather than render a believable "0.00%" integration rate that then gets
  // persisted into the quote audit trail. A caller that legitimately wants a
  // pre-first-authored-quarter snapshot (e.g. a build/parity script) should
  // call snapshotFromSeed directly instead of going through this adapter.
  if (Object.keys(resolved.componentCostMicros).length === 0) {
    throw new Error(`No catalog data resolved for ${vendor} ${quarter}`);
  }
  return resolved;
}
